import React from 'react'
import { useTheme } from '@/context/ThemeContext'
import type { ActivityPanel as ActivityPanelType, RestrictedPanel, UnavailablePanel } from '../types'
import EmptyWorkspaceState from './EmptyWorkspaceState'

interface ActivityPanelProps {
  panel: ActivityPanelType | UnavailablePanel | RestrictedPanel
}

// "14 minutes ago" reads better than a timestamp in a feed this dense, and it makes
// the grouping legible — a collapsed row is one moment, not a range.
function relativeTime(iso: string): string {
  try {
    const diffMs = Date.now() - new Date(iso).getTime()
    const minutes = Math.round(diffMs / 60_000)
    if (minutes < 1) return 'Just now'
    if (minutes < 60) return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`
    const hours = Math.round(minutes / 60)
    if (hours < 24) return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`
    const days = Math.round(hours / 24)
    if (days === 1) return 'Yesterday'
    if (days < 7) return `${days} days ago`
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
  } catch {
    return iso
  }
}

const ActivityPanel: React.FC<ActivityPanelProps> = ({ panel }) => {
  const { currentTheme: t } = useTheme()

  // Defence in depth: DashboardPage already filters this panel out for members, and
  // the backend withholds the data — render nothing rather than an empty shell if a
  // restricted payload ever reaches here.
  if (panel.status === 'restricted') return null

  return (
    <section
      className="rounded-2xl border overflow-hidden min-w-0"
      style={{ backgroundColor: t.secondary, borderColor: t.border, boxShadow: t.cardShadow }}
      aria-label="Activity"
    >
      <div className="flex items-center justify-between gap-3 px-4 pt-3.5 pb-2">
        <h2 className="text-[13px] font-semibold" style={{ color: t.text }}>
          Activity
        </h2>
        <span className="text-[10.5px] font-semibold uppercase tracking-wider" style={{ color: t.mutedText }}>
          Owner · admin
        </span>
      </div>

      {panel.status === 'unavailable' ? (
        <div className="px-4 pb-4">
          <EmptyWorkspaceState reason={panel.reason} />
        </div>
      ) : panel.items.length === 0 ? (
        <p className="px-4 pb-5 pt-2 text-xs" style={{ color: t.mutedText }}>
          Nothing has happened in this workspace yet.
        </p>
      ) : (
        <div className="px-4 pb-3.5 pt-1 flex flex-col gap-2.5 max-h-72 overflow-y-auto custom-scrollbar">
          {panel.items.map((item) => (
            <div key={item.id} className="flex items-start gap-2.5 min-w-0">
              <span
                className="mt-[7px] h-[5px] w-[5px] shrink-0 rounded-full"
                style={{ backgroundColor: item.actorKind === 'zeph' ? t.accent : t.mutedText }}
              />
              <div className="min-w-0">
                <p className="text-[11.5px] leading-snug" style={{ color: t.text }}>
                  {/* `summary` already reflects `count` — the server supplies the
                      plural phrasing, since "qualified a contact (score 92)" can't be
                      turned into a sentence about twelve of them by inserting a number. */}
                  <span className="font-semibold">{item.actorName}</span> {item.summary}
                </p>
                <p className="text-[10.5px]" style={{ color: t.mutedText }}>
                  {relativeTime(item.occurredAt)}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  )
}

export default React.memo(ActivityPanel)
